import { FormEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { useProduct, useEditProductStore } from '../../stores';

import { PriceType, Product, ProductType, VatTypes } from '../../model/Product';

import { UnitTypes } from '../../utils/product';
import { Money } from '../../utils/currency';

import { Currency } from '../../constants/currency';
import { VAT_LIST } from '../../constants/vat';
import EanPrefixTypes from '../../constants/eanPrefix';

interface EditProductProps {
  productId?: string;
  open: boolean;
  onClose: () => void;
}

const EditProduct = observer(({ productId, open, onClose }: EditProductProps): JSX.Element => {
  const { list, editProduct } = useProduct();
  const editProductStore = useEditProductStore();

  const product = list?.find((pm) => pm.id === productId);

  const { t } = useTranslation();

  useEffect(() => {
    if (product) {
      editProductStore.setEditProduct(product);
    }
  }, [product]);

  const handleOnSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const alteredFormData: Product = {
      id: product?.id || '',
      price:
        editProductStore?.priceType === PriceType.STATIC
          ? new Money(editProductStore.price, Currency.SEK)
          : new Money(0, Currency.SEK),
      priceType: editProductStore.priceType,
      showDecimals: editProductStore.showDecimals,
      showInShop: editProductStore.showInShop,
      tax: editProductStore.tax,
      title: editProductStore.title,
      type: editProductStore.type,
      unitType: editProductStore.unitType,
      ean: {
        ...editProductStore.ean,
        decimals: editProductStore.ean.decimals || '0',
        type: editProductStore.ean.type || EanPrefixTypes.COUNTRYCODE,
      },
    };
    editProduct(alteredFormData).finally(() => onClose());
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1',
          width: 600,
          height: '100%',
          paddingRight: 4,
          paddingLeft: 4,
        }}
      >
        <Toolbar />
        <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={onClose}>
            <ChevronRightIcon />
          </IconButton>
        </Toolbar>
        <Typography variant="h4" paragraph gutterBottom>
          {t(`productScreen.editProduct`)}
        </Typography>
        <Box
          component="form"
          onSubmit={handleOnSubmit}
          sx={{ display: 'flex', flexDirection: 'column', flex: '1' }}
        >
          <Grid container spacing={2}>
            <Grid item>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="info"
                      checked={editProductStore?.showInShop || false}
                      onChange={(e) => editProductStore.setShowInShop(e.target.checked)}
                    />
                  }
                  label={t(`productScreen.showInShop`)}
                  sx={{ userSelect: 'none' }}
                />
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="productEAN"
                label={t('productScreen.eanNumber')}
                variant="outlined"
                value={editProductStore.ean.code || ''}
                placeholder="00000000000000"
                onChange={(e) => editProductStore.setEanCode(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="productName"
                label={t('productScreen.name')}
                variant="outlined"
                value={editProductStore.title || ''}
                onChange={(e) => editProductStore.setTitle(e.target.value)}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="priceTypeLabel">{t('productScreen.priceType')}</InputLabel>
                <Select
                  labelId="priceTypeLabel"
                  id="priceType"
                  value={editProductStore?.priceType}
                  defaultValue={editProductStore?.priceType}
                  label={t('productScreen.priceType')}
                  onChange={(e) => editProductStore.setPriceType(e.target.value)}
                  sx={{ textTransform: 'capitalize' }}
                >
                  {Object.entries(PriceType).map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {t(`priceTypes.${value}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {editProductStore?.priceType === PriceType.DYNAMIC && (
              <Grid item>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="info"
                        checked={editProductStore?.showDecimals || false}
                        onChange={(e) => editProductStore.setShowDecimals(e.target.checked)}
                      />
                    }
                    label={t('productScreen.showDecimals')}
                    sx={{ userSelect: 'none' }}
                  />
                </FormGroup>
              </Grid>
            )}

            {editProductStore?.priceType === PriceType.STATIC && (
              <Grid item xs={12}>
                <TextField
                  id="productPrice"
                  label={t('productScreen.price')}
                  variant="outlined"
                  type="number"
                  value={
                    Number.isFinite(editProductStore.price.amount)
                      ? editProductStore.price.asDecimal()
                      : ''
                  }
                  onChange={(e) => editProductStore.setPrice(parseFloat(e.target.value))}
                  inputProps={{ step: 0.01 }}
                  required
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="productTaxLabel">{t('productScreen.chooseVat')}</InputLabel>
                <Select
                  labelId="productTaxLabel"
                  id="productTax"
                  value={editProductStore?.tax}
                  defaultValue={editProductStore?.tax}
                  label={t('productScreen.chooseVat')}
                  onChange={(e) => editProductStore.setTax(e.target.value as VatTypes)}
                >
                  {VAT_LIST.vatList.map((value) => (
                    <MenuItem key={value} value={value}>
                      {`${value} %`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {editProductStore?.priceType !== PriceType.DYNAMIC && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="productUnitLabel">{t(`productScreen.unit`)}</InputLabel>
                  <Select
                    labelId="productUnitLabel"
                    id="productUnit"
                    value={editProductStore?.unitType}
                    defaultValue={editProductStore?.unitType}
                    label={t('productScreen.unit')}
                    onChange={(e) => editProductStore.setUnitType(e.target.value)}
                  >
                    {Object.entries(UnitTypes)
                      .filter(([key]) => key !== 'DEFAULT')
                      .map(([key, value]) => (
                        <MenuItem key={key} value={value.id}>
                          {value.unit}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="productTypeLabel">{t('productScreen.chooseType')}</InputLabel>
                <Select
                  labelId="productTypeLabel"
                  id="productType"
                  value={editProductStore?.type}
                  defaultValue={editProductStore?.type}
                  label={t('productScreen.chooseType')}
                  onChange={(e) => editProductStore.setType(e.target.value as ProductType)}
                >
                  {Object.entries(ProductType).map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {t(`productTypes.${value}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box
            sx={{
              marginTop: 'auto',
              marginLeft: 'auto',
              padding: '1rem 0',
            }}
          >
            <Button variant="contained" type="submit">
              {t('productScreen.save')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
});

export default EditProduct;
